import { FC, useState, useMemo } from "react";
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Empty,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Box, Calculator, Clock, DollarSign, Package, Ruler } from "lucide-react";
import { InventoryTemplate } from "~/services/inventory-templates/inventory-templates.types";
import {
  useGetPackingMetrics,
  useGetMaterialBreakdown,
  PackingMetric,
} from "~/services/service-metrics";
import { PackingTypes } from "~/utils/constants";

interface PackingCalculatorProps {
  inventoryItems: InventoryTemplate[];
  onCalculationComplete?: (results: PackingCalculationResult[]) => void;
}

export interface PackingCalculationResult {
  itemId: string;
  itemName: string;
  dimensions: { length: number; width: number; height: number };
  packingType: string;
  materialCost: number;
  laborCost: number;
  totalCost: number;
  estimatedMinutes: number;
}

interface SelectedItem {
  id: string;
  packingType: string;
}

export const PackingCalculator: FC<PackingCalculatorProps> = ({
  inventoryItems,
  onCalculationComplete,
}) => {
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [hourlyRate, setHourlyRate] = useState<number>(90);
  const [calculatedResults, setCalculatedResults] = useState<PackingCalculationResult[]>([]);

  const { data: packingMetrics, isLoading: isLoadingMetrics } = useGetPackingMetrics();

  // Filter to only show LOOSE items that need packing
  const looseItems = useMemo(() => {
    return inventoryItems.filter(
      (item) => !item.packingType || item.packingType === PackingTypes.LOOSE
    );
  }, [inventoryItems]);

  // Items that already have packing assigned
  const packedItems = useMemo(() => {
    return inventoryItems.filter(
      (item) => item.packingType && item.packingType !== PackingTypes.LOOSE
    );
  }, [inventoryItems]);

  // Get suitable packing types for an item based on dimensions
  const getSuitablePackingTypes = (item: InventoryTemplate) => {
    if (!packingMetrics || !item.length || !item.width || !item.height) return packingMetrics || [];

    return packingMetrics.filter((metric) => {
      if (!metric.maxLength && !metric.maxWidth && !metric.maxHeight) return true;
      return (
        (!metric.maxLength || item.length <= Number(metric.maxLength)) &&
        (!metric.maxWidth || item.width <= Number(metric.maxWidth)) &&
        (!metric.maxHeight || item.height <= Number(metric.maxHeight))
      );
    });
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (checked) {
      // Find best matching packing type based on dimensions
      const item = looseItems.find((i) => i.id === itemId);
      const suitable = item ? getSuitablePackingTypes(item) : [];
      const defaultType = suitable.length > 0 ? suitable[0].packingType : "";

      setSelectedItems((prev) => [...prev, { id: itemId, packingType: defaultType }]);
    } else {
      setSelectedItems((prev) => prev.filter((s) => s.id !== itemId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = looseItems.map((item) => {
        const suitable = getSuitablePackingTypes(item);
        return {
          id: item.id,
          packingType: suitable.length > 0 ? suitable[0].packingType : "",
        };
      });
      setSelectedItems(newSelected);
    } else {
      setSelectedItems([]);
    }
  };

  const handlePackingTypeChange = (itemId: string, packingType: string) => {
    setSelectedItems((prev) =>
      prev.map((s) => (s.id === itemId ? { ...s, packingType } : s))
    );
  };

  const calculateCosts = async () => {
    const results: PackingCalculationResult[] = [];

    for (const selected of selectedItems) {
      const item = looseItems.find((i) => i.id === selected.id);
      if (!item || !selected.packingType) continue;

      const metric = packingMetrics?.find((m) => m.packingType === selected.packingType);
      if (!metric) continue;

      const length = item.length || 0;
      const width = item.width || 0;
      const height = item.height || 0;

      // Calculate surface area with 5% buffer
      const bufferedL = length * 1.05;
      const bufferedW = width * 1.05;
      const bufferedH = height * 1.05;
      const surfaceAreaSqFt = (2 * (bufferedL * bufferedW + bufferedL * bufferedH + bufferedW * bufferedH)) / 144;

      // Material cost = surface area × rate
      const materialCost = surfaceAreaSqFt * Number(metric.baseRate);

      // Labor cost = minutes × hourly rate / 60
      const estimatedMinutes = Number(metric.estimatedMinutesPerItem) || 0;
      const laborCost = (estimatedMinutes / 60) * hourlyRate;

      const totalCost = Math.max(materialCost + laborCost, Number(metric.minimumCharge) || 0);

      results.push({
        itemId: item.id,
        itemName: item.name,
        dimensions: { length, width, height },
        packingType: selected.packingType,
        materialCost,
        laborCost,
        totalCost,
        estimatedMinutes,
      });
    }

    setCalculatedResults(results);
    onCalculationComplete?.(results);
  };

  // Totals
  const totals = useMemo(() => {
    return calculatedResults.reduce(
      (acc, r) => ({
        materialCost: acc.materialCost + r.materialCost,
        laborCost: acc.laborCost + r.laborCost,
        totalCost: acc.totalCost + r.totalCost,
        totalMinutes: acc.totalMinutes + r.estimatedMinutes,
      }),
      { materialCost: 0, laborCost: 0, totalCost: 0, totalMinutes: 0 }
    );
  }, [calculatedResults]);

  if (isLoadingMetrics) {
    return (
      <div className="flex justify-center py-8">
        <Spin />
      </div>
    );
  }

  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedItems.length === looseItems.length && looseItems.length > 0}
          indeterminate={selectedItems.length > 0 && selectedItems.length < looseItems.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: "select",
      width: 50,
      render: (_: any, record: InventoryTemplate) => (
        <Checkbox
          checked={selectedItems.some((s) => s.id === record.id)}
          onChange={(e) => handleSelectItem(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <span className="font-medium">{name}</span>
      ),
    },
    {
      title: "Dimensions",
      key: "dimensions",
      render: (_: any, record: InventoryTemplate) => (
        <span className="text-sm text-gray-500">
          {record.length && record.width && record.height
            ? `${record.length}×${record.width}×${record.height}"`
            : "—"}
        </span>
      ),
    },
    {
      title: "Packing Type",
      key: "packingType",
      width: 200,
      render: (_: any, record: InventoryTemplate) => {
        const selected = selectedItems.find((s) => s.id === record.id);
        const suitable = getSuitablePackingTypes(record);

        if (!selected) {
          return <Tag color="orange">LOOSE</Tag>;
        }

        return (
          <Select
            size="small"
            className="w-full"
            value={selected.packingType || undefined}
            onChange={(value) => handlePackingTypeChange(record.id, value)}
            placeholder="Select packing..."
            options={suitable.map((m) => ({
              value: m.packingType,
              label: (
                <span>
                  {m.packingType}
                  <span className="text-gray-400 ml-2">${Number(m.minimumCharge).toFixed(0)}</span>
                </span>
              ),
            }))}
          />
        );
      },
    },
  ];

  const resultColumns = [
    {
      title: "Item",
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: "Packing",
      dataIndex: "packingType",
      key: "packingType",
      render: (type: string) => <Tag color="purple">{type}</Tag>,
    },
    {
      title: "Materials",
      dataIndex: "materialCost",
      key: "materialCost",
      render: (cost: number) => `$${cost.toFixed(2)}`,
    },
    {
      title: "Labor",
      dataIndex: "laborCost",
      key: "laborCost",
      render: (cost: number) => `$${cost.toFixed(2)}`,
    },
    {
      title: "Time",
      dataIndex: "estimatedMinutes",
      key: "estimatedMinutes",
      render: (min: number) => `${min} min`,
    },
    {
      title: "Total",
      dataIndex: "totalCost",
      key: "totalCost",
      render: (cost: number) => <strong>${cost.toFixed(2)}</strong>,
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium flex items-center">
            <Calculator size={20} className="mr-2 text-purple-600" />
            Packing Cost Calculator
          </h3>
          <p className="text-gray-500 text-sm">
            Select loose items and assign packing types to calculate costs
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <label className="text-xs text-gray-500 block">Labor Rate</label>
            <InputNumber
              size="small"
              value={hourlyRate}
              onChange={(v) => setHourlyRate(v || 90)}
              prefix="$"
              addonAfter="/hr"
              min={0}
              step={5}
            />
          </div>
        </div>
      </div>

      {/* Already Packed Items */}
      {packedItems.length > 0 && (
        <Card size="small" className="bg-green-50">
          <div className="flex items-center text-green-700 mb-2">
            <Package size={16} className="mr-2" />
            <span className="font-medium">Already Packed ({packedItems.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {packedItems.map((item) => (
              <Tag key={item.id} color="green">
                {item.name} - {item.packingType}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* Loose Items Selection */}
      {looseItems.length > 0 ? (
        <>
          <Card size="small">
            <div className="flex items-center text-orange-600 mb-3">
              <Box size={16} className="mr-2" />
              <span className="font-medium">Loose Items ({looseItems.length})</span>
              <span className="text-gray-400 text-sm ml-2">— Select items to calculate packing costs</span>
            </div>
            <Table
              columns={columns}
              dataSource={looseItems}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </Card>

          {/* Calculate Button */}
          <div className="flex justify-end">
            <Button
              type="primary"
              icon={<Calculator size={16} />}
              onClick={calculateCosts}
              disabled={selectedItems.length === 0 || selectedItems.some((s) => !s.packingType)}
            >
              Calculate Packing Costs ({selectedItems.length} items)
            </Button>
          </div>
        </>
      ) : (
        <Empty
          description="No loose items found"
          className="py-8"
        >
          <p className="text-sm text-gray-500">
            All items already have packing types assigned
          </p>
        </Empty>
      )}

      {/* Results */}
      {calculatedResults.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <h4 className="font-medium text-green-800 mb-3 flex items-center">
            <DollarSign size={18} className="mr-2" />
            Packing Cost Summary
          </h4>

          <Table
            columns={resultColumns}
            dataSource={calculatedResults}
            rowKey="itemId"
            size="small"
            pagination={false}
          />

          <Divider className="my-3" />

          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded p-3 border">
              <div className="text-xs text-gray-500">Materials</div>
              <div className="text-lg font-bold">${totals.materialCost.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded p-3 border">
              <div className="text-xs text-gray-500">Labor</div>
              <div className="text-lg font-bold">${totals.laborCost.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded p-3 border">
              <div className="text-xs text-gray-500">Total Time</div>
              <div className="text-lg font-bold">{totals.totalMinutes} min</div>
            </div>
            <div className="bg-green-100 rounded p-3 border border-green-300">
              <div className="text-xs text-green-600">Total Cost</div>
              <div className="text-xl font-bold text-green-700">${totals.totalCost.toFixed(2)}</div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
